import { useMemo } from 'react';
import { _ } from '/@/locale';
import { comparePositioningBuckets, type PositioningBucket } from '../../dashboard/rv/components/product-catalog';
import { type CornCycle, CornCycleChoices } from '../components/product-card';

const useProductsFilterOptions = (products: any[]) => {
    const technologyOptions = useMemo(() => {
        const technologies: Record<number, string> = {};
        for (const product of products) {
            for (const tech of product.technologies || []) {
                technologies[tech.id] = tech.name;
            }
        }
        return technologies;
    }, [products]);
    const technologyOptionsArray = useMemo(() => Object.entries(technologyOptions), [technologyOptions]);

    const zoneOptions = useMemo(() => {
        const zones: Record<number, string> = {};
        for (const product of products) {
            if (!product.catalogZoneId) continue;
            zones[product.catalogZoneId] = _(product.catalogZone);
        }
        return zones;
    }, [products]);
    const zoneOptionsArray = useMemo(() => Object.entries(zoneOptions), [zoneOptions]);

    const brandOptions = useMemo(() => {
        const brands: Record<number, string> = {};
        for (const product of products) {
            if (!product?.brand?.id) continue;
            brands[product.brand.id] = product.brand.name;
        }
        return brands;
    }, [products]);
    const brandOptionsArray = useMemo(() => Object.entries(brandOptions), [brandOptions]);

    const typeOptions = useMemo(() => {
        const types: Record<number, string> = {};
        for (const product of products) {
            if (!product?.type) continue;
            types[product?.type?.id] = _(product?.type);
        }
        return types;
    }, [products]);
    const typeOptionsArray = useMemo(() => Object.entries(typeOptions), [typeOptions]);

    const cropOptions = useMemo(() => {
        const crops: Record<number, string> = {};
        for (const product of products) {
            if (!product?.crops) continue;
            for (const crop of product.crops) {
                crops[crop.id] = _(crop.name);
            }
        }
        return crops;
    }, [products]);
    const cropOptionsArray = useMemo(() => Object.entries(cropOptions), [cropOptions]);

    const cycleOptions = useMemo(() => {
        const cycles: Record<CornCycle, string> = {};
        for (const product of products) {
            if (!product?.cycle) continue;
            cycles[product.cycle as CornCycle] = CornCycleChoices[product?.cycle as CornCycle];
        }
        return cycles;
    }, [products]);
    const cycleOptionsArray = useMemo(() => Object.entries(cycleOptions), [cycleOptions]);

    // Sorted ascending by the buckets' numeric bounds, so the options read
    // low-to-high: "< 6,000", "6000 - 9,000", "9000 - 12,000", "> 12,000".
    const sortedPositioningBuckets = useMemo(() => {
        // A product can cover several canonical buckets (e.g. "< 60" and
        // "60 - 90"); add each so the filter only ever offers the 4 buckets.
        const buckets = new Map<string, PositioningBucket>();
        for (const product of products) {
            for (const bucket of (product?.positioningBuckets || []) as PositioningBucket[]) {
                if (bucket?.label && bucket.label !== '-' && !buckets.has(bucket.label)) {
                    buckets.set(bucket.label, bucket);
                }
            }
        }
        return Array.from(buckets.values()).sort(comparePositioningBuckets);
    }, [products]);

    const positioningOptions = useMemo(
        () => Object.fromEntries(sortedPositioningBuckets.map(({ label }) => [label, label])),
        [sortedPositioningBuckets]
    );
    const positioningOptionsArray = useMemo(
        () => sortedPositioningBuckets.map(({ label }) => [label, label] as [string, string]),
        [sortedPositioningBuckets]
    );

    return {
        technologyOptions,
        technologyOptionsArray,
        zoneOptions,
        zoneOptionsArray,
        brandOptions,
        brandOptionsArray,
        typeOptions,
        typeOptionsArray,
        cropOptions,
        cropOptionsArray,
        cycleOptions,
        cycleOptionsArray,
        positioningOptions,
        positioningOptionsArray,
    };
};

export default useProductsFilterOptions;
